//@storybook-skip this is a container
import { events } from "@constants/tracking";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { handleNavigateBack } from "@helpers/common";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTemplate } from "@hooks/useAppTemplate";
import { getReportSkills } from "@memorang/helpers/report";
import type { BaseItemSchema, ReportDetails } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { SessionReportApplet } from "@memorang/ui/components/SessionReport/SessionReportApplet";
import { TipCallout } from "@memorang/ui/components/TipCallout";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import { useQuery } from "@tanstack/react-query";
import { router, useLocalSearchParams } from "expo-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
	LANGUAGE_LEARNING_REPORT_USE_CASE,
	buildReportTipData,
	getReportSessionTip,
	insightsReportTipSchema,
	reportTipSchema,
	withTipAppFraming,
} from "../../tips/language-learning";
import { tipQueryOptions } from "../../tips/tipQueryOptions";
import type { GoToReviewSession } from "../hooks/useGoToReviewSession";
import type { TrackReportEvent } from "../hooks/useReportAnalytics";
import { useSessionReportItemTypeDetailDialog } from "../hooks/useSessionReportItemTypeDetailDialog";
import { useSessionReportNextAction } from "../hooks/useSessionReportNextAction";
import { useSessionReportPremiumCard } from "../hooks/useSessionReportPremiumCard";
import { useSessionReportProficiencyEstimates } from "../hooks/useSessionReportProficiencyEstimates";
import { useSessionReportReviewAnswersDialog } from "../hooks/useSessionReportReviewAnswersDialog";
import { SessionInsightsTipSlot } from "./SessionInsightsTipSlot";
import { SessionTipSlot } from "./SessionTipSlot";

type Props = {
	reportDetails?: ReportDetails;
	isLoading?: boolean;
	handleContinue: () => void;
	trackReportEvent: TrackReportEvent;
	isSummativeTest: boolean;
	isDiagnostic: boolean;
	sessionItems?: BaseItemSchema[];
	sessionItemsLoading?: boolean;
	shouldRenderItemReviewCard: boolean;
	onReview: GoToReviewSession;
	isReviewLoading?: boolean;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
export const LanguageLearningReportScreen = (props: Props) => {
	const {
		reportDetails,
		isLoading,
		handleContinue,
		trackReportEvent,
		isSummativeTest,
		isDiagnostic,
		sessionItems = [],
		sessionItemsLoading = false,
		shouldRenderItemReviewCard,
		onReview,
		isReviewLoading = false,
	} = props;
	const { i18n } = useTranslation();
	const { template, isLanguageLearningApp } = useAppTemplate();
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const insets = useSafeAreaInsets();

	const {
		currentExamContext: { examId, examName: examContextName },
	} = useExamContext();

	const appId = useAppStore((s) => s.app.id);
	const tenantName = useAppStore((s) => s.tenant.name);
	const maxScaledScore = useAppStore((s) => s.app.maxScaledScore);

	const { onSelectQuestionType, itemTypeDetailDialog } =
		useSessionReportItemTypeDetailDialog({
			onReview,
			isReviewLoading,
			trackReportEvent,
		});

	const { onOpenReviewAnswers, reviewAnswersDialog } =
		useSessionReportReviewAnswersDialog({
			onReview,
			isReviewLoading,
			trackReportEvent,
		});

	const isReportLoading = Boolean(isLoading || !reportDetails);
	const { id: routeSessionId, canGoBack } = useLocalSearchParams<{
		id: string;
		canGoBack?: string;
	}>();
	const sessionId = reportDetails?.id ?? routeSessionId ?? "";

	const { premium: premiumCard, onUpgrade } =
		useSessionReportPremiumCard(sessionId);

	const { userPublicMetadataId } = useAuthContext();

	const reportTags = reportDetails?.tagDetails ?? [];
	const reportTopics = reportDetails?.topicsFilterContext?.includes("REPORTS")
		? (reportDetails?.topics ?? []).map((topic) => topic.toLowerCase())
		: [];
	const proficiencyEstimates = useSessionReportProficiencyEstimates({
		isSummativeTest,
		tags: reportTags,
		topics: reportTopics,
		sessionItems,
	});

	// `reportTopics` is rebuilt every render, so key the memo on the resolved ids
	// instead — an unstable array here re-runs the recommendation ranking each render.
	const skillTagIdsKey = getReportSkills(reportTags, reportTopics)
		.map((skill) => skill.id)
		.join(",");
	const skillTagIds = useMemo(
		() => (skillTagIdsKey ? skillTagIdsKey.split(",").map(Number) : []),
		[skillTagIdsKey],
	);
	const sessionTagIds = reportTags.map((tag) => tag.id);
	const { nextActionSlot, nextActionDialog } = useSessionReportNextAction({
		sessionId,
		skillTagIds,
		sessionTagIds,
		isSummativeTest,
	});

	const tipEnabled =
		isLanguageLearningApp &&
		!!reportDetails &&
		!reportDetails?.isMigratedProgress;
	const reportTipData = useMemo(
		() =>
			tipEnabled
				? withTipAppFraming(appId, template, buildReportTipData(reportDetails))
				: undefined,
		[tipEnabled, reportDetails, appId, template],
	);
	const sessionInsightsEnabled = Boolean(
		useAppStore((s) => s.reportView?.ai?.sessionInsights),
	);
	const reportTip = useQuery(
		tipQueryOptions({
			useCase: LANGUAGE_LEARNING_REPORT_USE_CASE,
			data: reportTipData,
			dataKey: sessionId,
			schema: sessionInsightsEnabled
				? insightsReportTipSchema
				: reportTipSchema,
			userId: userPublicMetadataId,
			sessionId: Number(sessionId) || undefined,
			outputLanguage: i18n.language,
			enabled: tipEnabled,
		}),
	);

	const renderSessionTipSlot = () => {
		if (!(tipEnabled && reportDetails)) {
			return undefined;
		}
		const slotProps = {
			reportDetails,
			examId: examId ?? "",
			appId: appId ?? "",
			tenantName: tenantName ?? "",
			outputLanguage: i18n.language,
			handleTracking: trackReportEvent,
		};
		return sessionInsightsEnabled ? (
			<SessionInsightsTipSlot {...slotProps} />
		) : (
			<SessionTipSlot
				{...slotProps}
				previewText={getReportSessionTip(reportTip.data)}
				isLoading={reportTip.isLoading}
				isError={reportTip.isError}
			/>
		);
	};

	const usePoints =
		reportDetails?.numTotalPoints != null && reportDetails.numTotalPoints > 0;
	const numTotalPoints = Number(reportDetails?.numTotalPoints ?? 0);
	const numPointsEarned = Number(reportDetails?.numPointsEarned ?? 0);

	return (
		<Box flex={1} backgroundColor={theme.colors.background}>
			<Box height={insets.top} backgroundColor={theme.colors.surface} />
			<SessionReportApplet
				value={{
					isLoading: isReportLoading || sessionItemsLoading,
					session: {
						examName: reportDetails?.examName ?? "",
						examContextName,
						sessionId: reportDetails?.id ?? "",
						sessionDate: reportDetails?.sessionDate ?? "",
						isSummativeTest,
						isDiagnostic,
					},
					report: {
						tags: reportTags,
						topics: reportTopics,
						sessionItems: sessionItems,
						timeElapsed: reportDetails?.timeElapsed ?? 0,
						numItems: reportDetails?.numQuestions ?? 0,
						overallScore: {
							score: usePoints
								? numPointsEarned
								: Number(reportDetails?.score ?? 0),
							maxScore: usePoints ? numTotalPoints : maxScaledScore,
						},
						overallPoints: usePoints
							? {
									earned: numPointsEarned,
									total: numTotalPoints,
								}
							: undefined,
						proficiencyEstimates,
					},
					cards: {
						showItemReview: shouldRenderItemReviewCard,
						...(premiumCard ? { premium: premiumCard } : {}),
					},
					options: {
						onBack:
							isMobile && canGoBack === "true"
								? () => {
										trackReportEvent(events.reportExit, { source: "back" });
										handleNavigateBack("/(protected)/(tabs)/home");
									}
								: undefined,
						onContinue: () => {
							trackReportEvent(events.reportExit, { source: "go_home" });
							handleContinue();
						},
						onViewAllItemTypes: (filter?: string) => {
							trackReportEvent(events.reportViewAllClicked, {
								type: "item_types",
								skill: filter,
							});
							router.push({
								pathname: "/(protected)/session/[id]/report/types",
								params: {
									id: sessionId,
									...(filter ? { filter } : {}),
								},
							});
						},
						onNavigateToItems: () => {
							if (!reportDetails) {
								return;
							}
							trackReportEvent(events.reportViewAllClicked, {
								type: "answers",
							});
							router.push({
								pathname: "/(protected)/session/[id]/report/items",
								params: { id: sessionId },
							});
						},
						onUpgrade,
						onReview,
						onOpenReviewAnswers,
						onSelectQuestionType,
						isReviewLoading,
					},
				}}
				nextActionSlot={nextActionSlot}
				sessionTipSlot={renderSessionTipSlot()}
				itemTypesTipSlot={
					tipEnabled ? (
						<TipCallout
							text={reportTip.data?.itemTypes}
							isLoading={reportTip.isLoading}
							isError={reportTip.isError}
						/>
					) : undefined
				}
			>
				<SessionReportApplet.Header />
				<Divider />
				<ScrollContainer style={{ flex: 1 }}>
					<Box
						flex={1}
						width="100%"
						alignSelf="center"
						paddingHorizontal={isMobile ? 0 : 32}
						paddingVertical={32}
					>
						<SessionReportApplet.Body />
					</Box>
				</ScrollContainer>
				<Divider />
				<SessionReportApplet.Footer />
			</SessionReportApplet>
			{itemTypeDetailDialog}
			{reviewAnswersDialog}
			{nextActionDialog}
		</Box>
	);
};
